import { useMemo } from 'react';

import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
  OpportunityRelatedInclusion,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { withChecklistItemMetadata } from './checklistItemMetadata';
import { getLatestSyncedAt } from './shared/checklistUtils';
import {
  buildBlackoutCoverageRow,
  buildBlackoutRows,
  calculateBlackoutCoverage,
} from './tabs/blackoutDates/blackoutRules';
import { buildBundledExperienceRows } from './tabs/bundledExperiences/bundledExperiencesRules';
import { buildCampaignRows } from './tabs/campaign/campaignRules';
import {
  buildDealOptionsInclusionsDealRows,
  buildDealOptionsInclusionsRows,
} from './tabs/dealOptionsInclusions/dealOptionsInclusionsRules';
import { buildDiscountsMarginRows } from './tabs/discountsMargin/discountsMarginRules';
import { buildEgsCapacityRows, buildEgsMarginRows } from './tabs/egs/egsRules';
import { buildSetupChecklistRules } from './tabs/setup/setupRules';
import type { SetupRuleInput } from './tabs/setup/types';
import { buildSurchargeTableRows, buildSurchargesRows } from './tabs/surcharges/surchargesRules';
import type { BlackoutDateRange } from './tabs/surcharges/types';
import { buildTaxesPaymentsRows } from './tabs/taxesPayments/taxesPaymentsRules';
import type { ChecklistCategoryId } from './types';

interface Input {
  blackoutDates?: Array<BlackoutDateRange>;
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityContract?: App.Contract;
  opportunityError?: unknown;
  packages?: Array<App.Package>;
  propertyError?: unknown;
  propertyHasTaxesPayableAtProperty: boolean;
  propertyId?: string;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  relatedInclusions?: Array<OpportunityRelatedInclusion>;
  setupInput: SetupRuleInput;
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}

export function useCMChecklistRows({
  blackoutDates,
  offer,
  opportunity,
  opportunityContract,
  opportunityError,
  packages,
  propertyError,
  propertyHasTaxesPayableAtProperty,
  propertyId,
  propertyRoomTypes,
  relatedDeals,
  relatedExtraGuestSurcharges,
  relatedInclusions,
  setupInput,
  surcharges,
  travelFromDate,
  travelToDate,
}: Input) {
  const opportunityTravelFrom = opportunity?.key_dates?.expiry_date_travel_from__c ?? travelFromDate;
  const opportunityTravelTo = opportunity?.key_dates?.travel_to__c ?? travelToDate;
  const opportunityBlackoutDates = opportunity?.black_out_dates;
  const dealCurrency = getOfferCurrency(packages);

  const blackoutDateRows = useMemo(
    () =>
      buildBlackoutRows({
        blackOutDates: opportunity?.black_out_dates,
        hasSourceError: !!opportunityError,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [opportunity?.black_out_dates, opportunityError, opportunityTravelFrom, opportunityTravelTo],
  );
  const blackoutCoverage = useMemo(
    () =>
      calculateBlackoutCoverage({
        blackOutDates: blackoutDateRows,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [blackoutDateRows, opportunityTravelFrom, opportunityTravelTo],
  );
  const blackoutRows = useMemo(
    () => withChecklistItemMetadata('blackoutDates', [...blackoutDateRows, buildBlackoutCoverageRow(blackoutCoverage)]),
    [blackoutCoverage, blackoutDateRows],
  );

  const setupRows = useMemo(
    () => withChecklistItemMetadata('setup', buildSetupChecklistRules(setupInput)),
    [setupInput],
  );
  const marginRows = useMemo(
    () => withChecklistItemMetadata('egs', buildEgsMarginRows(relatedExtraGuestSurcharges)),
    [relatedExtraGuestSurcharges],
  );
  const capacityRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'egs',
        buildEgsCapacityRows({
          hasEgsMarginRows: marginRows.length > 0,
          packages,
          propertyRoomTypes,
          relatedExtraGuestSurcharges,
        }),
      ),
    [marginRows.length, packages, propertyRoomTypes, relatedExtraGuestSurcharges],
  );
  const baseSurchargesRows = useMemo(
    () => withChecklistItemMetadata('surcharges', buildSurchargesRows({ surcharges, travelFromDate, travelToDate })),
    [surcharges, travelFromDate, travelToDate],
  );
  const surchargeTableRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'surcharges',
        buildSurchargeTableRows({ blackoutDates, packages, surcharges, travelFromDate, travelToDate }),
      ),
    [blackoutDates, packages, surcharges, travelFromDate, travelToDate],
  );
  const propertyDataAvailable = propertyId !== undefined && !propertyError;
  const taxesPaymentsRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'taxesPayments',
        buildTaxesPaymentsRows({
          cityOtherTax: opportunityContract?.city_other_tax__c,
          cityTaxComments: opportunityContract?.city_tax_comments__c,
          hasPropertyTaxesPayableAtProperty: propertyHasTaxesPayableAtProperty,
          opportunityDataAvailable: !!opportunityContract,
          paymentMethod: opportunityContract?.payment_method__c,
          paymentMilestone: opportunityContract?.payment_milestone__c,
          prizeOrderId: opportunityContract?.prize_order_id__c,
          propertyDataAvailable,
        }),
      ),
    [opportunityContract, propertyHasTaxesPayableAtProperty, propertyDataAvailable],
  );
  const campaignRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'campaign',
        buildCampaignRows({ offer, opportunity, opportunityDeals: relatedDeals, packages, setupInput }),
      ),
    [offer, opportunity, packages, relatedDeals, setupInput],
  );
  const discountsMarginRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'discountsMargin',
        buildDiscountsMarginRows({ opportunityDeals: relatedDeals, packages }),
      ),
    [relatedDeals, packages],
  );
  const bundledExperienceRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'bundledExperiences',
        buildBundledExperienceRows({
          dealCurrency,
          hasOpportunitySourceError: !!opportunityError,
          hotelBlackoutDates: opportunityBlackoutDates,
          hotelTravelFrom: opportunityTravelFrom,
          hotelTravelTo: opportunityTravelTo,
          relatedDeals,
        }),
      ),
    [
      dealCurrency,
      opportunityBlackoutDates,
      opportunityError,
      opportunityTravelFrom,
      opportunityTravelTo,
      relatedDeals,
    ],
  );

  const dealOptionsInclusionsRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'dealOptionsInclusions',
        buildDealOptionsInclusionsRows({
          dealDataAvailable: relatedDeals !== undefined,
          relatedDeals: relatedDeals ?? [],
          relatedInclusions: relatedInclusions ?? [],
          relatedInclusionsAvailable: relatedInclusions !== undefined,
        }),
      ),
    [relatedDeals, relatedInclusions],
  );
  const dealOptionsInclusionsDealRows = useMemo(
    () =>
      buildDealOptionsInclusionsDealRows({
        flatRows: dealOptionsInclusionsRows,
        relatedDeals: relatedDeals ?? [],
      }),
    [dealOptionsInclusionsRows, relatedDeals],
  );

  const categorySyncedAt = useMemo<Partial<Record<ChecklistCategoryId, string>>>(() => {
    const dealsSyncedAt = relatedDeals?.map((deal) => deal.synced_at);
    const inclusionsSyncedAt = relatedInclusions?.map((inclusion) => inclusion.synced_at);
    const extraGuestSurchargesSyncedAt = relatedExtraGuestSurcharges?.map((surcharge) => surcharge.synced_at);
    const blackoutDatesSyncedAt = opportunity?.black_out_dates?.map((blackoutDate) => blackoutDate.synced_at);

    return {
      blackoutDates: getLatestSyncedAt(blackoutDatesSyncedAt),
      bundledExperiences: getLatestSyncedAt(dealsSyncedAt, blackoutDatesSyncedAt),
      campaign: getLatestSyncedAt(opportunity?.synced_at, dealsSyncedAt),
      dealOptionsInclusions: getLatestSyncedAt(dealsSyncedAt, inclusionsSyncedAt),
      discountsMargin: getLatestSyncedAt(dealsSyncedAt),
      egs: getLatestSyncedAt(extraGuestSurchargesSyncedAt),
      setup: getLatestSyncedAt(opportunity?.synced_at),
      taxesPayments: getLatestSyncedAt(opportunity?.synced_at),
    };
  }, [
    opportunity?.black_out_dates,
    opportunity?.synced_at,
    relatedDeals,
    relatedExtraGuestSurcharges,
    relatedInclusions,
  ]);

  return {
    baseSurchargesRows,
    blackoutCoverage,
    blackoutRows,
    bundledExperienceRows,
    campaignRows,
    capacityRows,
    categorySyncedAt,
    discountsMarginRows,
    dealOptionsInclusionsDealRows,
    dealOptionsInclusionsRows,
    marginRows,
    opportunityTravelFrom,
    opportunityTravelTo,
    setupRows,
    surchargeTableRows,
    taxesPaymentsRows,
  };
}

function getOfferCurrency(packages?: Array<App.Package>) {
  return packages?.find((offerPackage) => offerPackage.cost_currency)?.cost_currency;
}
